/**
 * Grommet Theme
 *
 * https://v2.grommet.io/globaltheme
 */

import { DropType } from 'grommet';
import { grommet, ThemeType } from 'grommet/themes';
import { Colors } from 'grommet/themes/base';
import { ColorType, deepMerge } from 'grommet/utils';

const talonColors: Colors & { 'brand-gradient': ColorType; 'accent-5': ColorType; 'accent-6': ColorType } = {
  brand: {
    light: '#0d48a1',
    dark: '#0d48a1',
  },
  'brand-gradient': {
    light: 'var(--brand-gradient)',
    dark: 'var(--brand-gradient)',
  },
  'accent-1': '#fff',
  'accent-2': {
    light: '#0d92a1',
    dark: '#0d92a1',
  },
  'accent-3': {
    light: '#a10d48',
    dark: '#a10d48',
  },
  'accent-4': {
    light: '#660da1',
    dark: '#660da1',
  },
  'accent-5': {
    light: '#1c0da1',
    dark: '#1c0da1',
  },
  'accent-6': {
    light: '#a1660d',
    dark: '#a1660d',
  },
};

export const borderRadiusSize = '15px';

const grommetTheme = deepMerge(grommet, {
  global: {
    colors: {
      ...talonColors,
      'hint-background': '#DAB8F7FF',
      focus: talonColors.brand,
      alert: 'red',
    },
    focus: {
      outline: {
        size: '0px',
      },
    },
  },
  accordion: {
    border: undefined,
  },
  tip: {
    content: {
      background: {
        color: 'light-1',
        opacity: 1,
      },
      round: borderRadiusSize,
      border: { size: '1px', color: 'light-4' },
      pad: 'small',
      margin: 'small',
    },
  },
  tab: {
    color: 'text-strong',
    active: {
      background: 'background',
    },
    background: 'background-contrast',
    border: {
      side: 'bottom',
      color: 'border',
      active: {
        color: 'border-strong',
      },
      disabled: {
        color: 'border-weak',
      },
      hover: {
        color: 'border',
      },
    },
    disabled: {
      color: 'text-weak',
    },
    hover: {
      background: 'accent-2',
      color: 'text',
    },
    pad: 'small',
    margin: {
      horizontal: 'none',
    },
  },
  tabs: {
    gap: 'small',
  },
  textArea: {
    extend: () => `
    resize: none;
    font-size: 1.1rem;
    padding: 1rem 2rem 1rem 2rem;
    border-radius: 0.5rem;
    border: 1px solid #d9d9e3;
    background: #ffffff;
    color: #000;
    outline: none;  
    `,
  },
  text: {
    xsmall: {
      size: '12px',
      height: '18px',
    },
    small: {
      size: '14px',
      height: '20px',
    },
    medium: {
      size: '18px',
      height: '24px',
    },
    large: {
      size: '22px',
      height: '28px',
    },
    xlarge: {
      size: '26px',
      height: '32px',
    },
    xxlarge: {
      size: '34px',
      height: '40px',
    },
  },
  dataTable: {
    header: {
      color: 'white',
      font: {
        size: 'xsmall',
      },
      gap: 'none',
    },

    body: {
      row: {
        extend: 'height: 49px',
      },
    },
  },
  formField: {
    label: {
      requiredIndicator: true,
    },
  },
} as ThemeType);

export default grommetTheme;

export const tooltipProps: DropType = {
  background: 'light-1',
  round: borderRadiusSize,
  pad: 'small',
  margin: 'small',
  border: { size: '1px', color: 'light-4' },
  width: { max: grommetTheme.global?.size?.medium },
};
